import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  Heading,
  Input,
  VStack,
  HStack,
  Text,
  InputGroup,
  InputRightElement,
  IconButton,
  Spinner,
  Flex,
} from '@chakra-ui/react';
import { BsX } from 'react-icons/bs';
import { SingleDatepicker } from 'chakra-dayzed-datepicker';
import FormRow from '@/features/user/FormRow.jsx';
import { useGetBannerQuery, useAddBannerMutation, useDeleteBannerMutation } from '@/app/services/nucleus';
import { useNotification } from '@/hooks/useNotification';
import { datePickerConfig } from '@/features/common/datePickerConfig';
import { isRealString } from '@/utils/helpers';

const defaultFormData = {
  bannerText: '',
  bannerHref: '',
  hrefText: '',
  expiration: null,
  errors: {},
};

const MAX_TEXT_LENGTH = 255;
const MAX_HREF_TEXT_LENGTH = 35;

const inputStyles = {
  focusBorderColor: 'brand.700',
  sx: { '::placeholder': { color: 'gray.400' } },
};

const validateBannerText = (text) => {
  if (!text.trim()) {
    return 'Text field is required';
  }
  if (text.length > MAX_TEXT_LENGTH) {
    return `Text field must be ${MAX_TEXT_LENGTH} characters or less`;
  }
  return null;
};

const validateHrefText = (text) => {
  if (text?.length > MAX_HREF_TEXT_LENGTH) {
    return `Link Text field must be ${MAX_HREF_TEXT_LENGTH} characters or less`;
  }
  return null;
};

const validateUrl = (url) => {
  if (!url || url.startsWith('https://') || url.startsWith('/')) return null;
  return 'URL must start with https:// for external links or / for internal links';
};

const BannerForm = () => {
  const [formData, setFormData] = useState({ ...defaultFormData });

  const { data: bannerData, isLoading } = useGetBannerQuery();
  const [addBanner, { isLoading: addLoading, isError: addError, error: addErrorMsg, isSuccess: addSuccess, reset: addReset }] =
    useAddBannerMutation();
  const [deleteBanner, { isLoading: deleteLoading, isError: deleteError, error: deleteErrorMsg, isSuccess: deleteSuccess, reset: deleteReset }] =
    useDeleteBannerMutation();

  useEffect(() => {
    // Populate form with existing banner
    if (bannerData) {
      setFormData({
        bannerText: bannerData.text,
        bannerHref: bannerData.href,
        hrefText: bannerData.href_text,
        expiration: bannerData.expires_at ? new Date(bannerData.expires_at) : null,
      });
    }
  }, [bannerData]);

  useNotification(addSuccess, addReset, addSuccess, 'success', 'Banner added successfully');
  useNotification(addError, addReset, addErrorMsg, 'error', 'Failed to add banner');
  useNotification(deleteSuccess, deleteReset, deleteSuccess, 'success', 'Banner deleted successfully');
  useNotification(deleteError, deleteReset, deleteErrorMsg, 'error', 'Failed to delete banner');

  const handleInputChange = useCallback((key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
      errors: {
        ...prev.errors,
        ...(key === 'bannerText' && { bannerText: validateBannerText(value) }),
        ...(key === 'bannerHref' && { bannerHref: validateUrl(value) }),
        ...(key === 'hrefText' && { hrefText: validateHrefText(value) }),
      },
    }));
  }, []);

  const isFormValid = () => !validateBannerText(formData.bannerText) && !validateUrl(formData.bannerHref) && !validateHrefText(formData.hrefText);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid()) {
      return;
    }

    const bannerPayload = {
      text: formData.bannerText,
      href: formData.bannerHref || null,
      href_text: formData.hrefText || null,
      expires_at: formData.expiration ? formData.expiration.toISOString() : null,
    };

    try {
      await addBanner(bannerPayload).unwrap();
    } catch (error) {
      console.error('Failed to add banner:', error);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteBanner().unwrap();
      setFormData({ ...defaultFormData });
    } catch (error) {
      console.error('Failed to delete banner:', error);
    }
  };

  const handleCancel = () => {
    setFormData({
      bannerText: bannerData?.text || defaultFormData.bannerText,
      bannerHref: bannerData?.href || defaultFormData.bannerHref,
      hrefText: bannerData?.href_text || defaultFormData.hrefText,
      expiration: bannerData?.expires_at ? new Date(bannerData.expires_at) : defaultFormData.expiration,
      errors: {},
    });
  };

  if (isLoading)
    return (
      <Flex alignItems="center" justifyContent="center" pos={'absolute'} height={'100%'} width={'100%'}>
        <Spinner color="brand.900" size="lg" mt={40} />
      </Flex>
    );

  return (
    <Box>
      <Heading as="h4" size="md" fontWeight="bold" color="brand.900">
        Banner
      </Heading>
      <VStack spacing={4} align={'start'} mt={6}>
        <form onSubmit={handleSubmit}>
          <VStack spacing={4}>
            {/* Banner Text Field */}
            <FormControl isInvalid={formData.errors?.bannerText} isRequired>
              <FormRow label="Text" templateColumns={{ base: '1fr', md: '1fr 8fr' }} width={'100%'}>
                <Input
                  value={formData.bannerText}
                  onChange={(e) => handleInputChange('bannerText', e.target.value)}
                  placeholder="Enter banner text"
                  maxLength={MAX_TEXT_LENGTH}
                  {...inputStyles}
                />
                <Box gridColumn="2" textAlign="right" mt={-3}>
                  <Text fontSize="sm">
                    {formData.bannerText.length}/{MAX_TEXT_LENGTH}
                  </Text>
                </Box>
              </FormRow>
            </FormControl>

            {/* Banner URL Field */}
            <FormControl isInvalid={formData.errors?.bannerHref}>
              <FormRow label="Link URL" templateColumns={{ base: '1fr', md: '1fr 8fr' }} width={'100%'}>
                <VStack spacing={1}>
                  <Input
                    value={formData.bannerHref}
                    onChange={(e) => handleInputChange('bannerHref', e.target.value)}
                    placeholder="https://example.com"
                    {...inputStyles}
                  />
                  {isRealString(formData.errors?.bannerHref) ? <FormErrorMessage>{formData.errors?.bannerHref}</FormErrorMessage> : null}
                </VStack>
              </FormRow>
            </FormControl>

            {/* Href Text Field */}
            <FormControl isInvalid={formData.errors?.hrefText}>
              <FormRow label="Link Text" templateColumns={{ base: '1fr', md: '1fr 8fr' }} width={'100%'}>
                <VStack spacing={2}>
                  <Input
                    value={formData.hrefText}
                    onChange={(e) => handleInputChange('hrefText', e.target.value)}
                    placeholder="Enter button text (e.g., Click Here)"
                    {...inputStyles}
                    maxLength={MAX_HREF_TEXT_LENGTH}
                  />
                </VStack>
                <Box gridColumn="2" textAlign="right" mt={-3}>
                  <Text fontSize="sm">
                    {formData.hrefText?.length ?? 0}/{MAX_HREF_TEXT_LENGTH}
                  </Text>
                </Box>
              </FormRow>
            </FormControl>

            {/* Expiration Date Field */}
            <FormControl>
              <FormRow label="Expires On" templateColumns={{ base: '1fr', md: '1fr 8fr' }} width={'100%'}>
                <InputGroup sx={{ zIndex: 100 }}>
                  <SingleDatepicker
                    id="expires_on"
                    name="expiration"
                    date={formData?.expiration || undefined}
                    minDate={new Date()}
                    onDateChange={(date) => handleInputChange('expiration', date || null)}
                    propsConfigs={datePickerConfig({ name: 'expiration' })}
                    configs={{ dateFormat: 'yyyy-MM-dd h:mm a' }}
                  />
                  {formData?.expiration && (
                    <InputRightElement>
                      <IconButton
                        onClick={() => handleInputChange('expiration', null)}
                        icon={<BsX />}
                        variant="ghost"
                        size="md"
                        aria-label="clear-expires_on"
                      />
                    </InputRightElement>
                  )}
                </InputGroup>
              </FormRow>
            </FormControl>
            <HStack spacing={4} pt={4} justifyContent="flex-end" width="100%">
              {bannerData && (
                <Button colorScheme="red" variant="outline" onClick={handleDelete} isLoading={deleteLoading} isDisabled={addLoading}>
                  Delete
                </Button>
              )}
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit" colorScheme="purple" isLoading={addLoading} isDisabled={!isFormValid()}>
                Submit
              </Button>
            </HStack>
          </VStack>
        </form>
      </VStack>
    </Box>
  );
};

export default BannerForm;
